import React, { useState, useEffect } from 'react';
import { AuditLog, AuditActionType, UserProfile } from '../types';
import { storage } from '../services/storage';
import { useModal } from './ui/ModalProvider';
import {
  Search, Filter, FileText, User, Calendar, Database, Eye, Download,
  RefreshCw, ChevronDown, ChevronUp
} from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

interface AdminAuditLogsProps {
  userProfile: UserProfile;
}

const ACTION_TYPE_COLORS: Record<AuditActionType, { bg: string; text: string; label: string }> = {
  INSERT: { bg: 'bg-green-100', text: 'text-green-800', label: 'Création' },
  UPDATE: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Modification' },
  DELETE: { bg: 'bg-red-100', text: 'text-red-800', label: 'Suppression' },
  UPSERT: { bg: 'bg-purple-100', text: 'text-purple-800', label: 'Création/Modif' }
};

const TABLE_NAMES = [
  'owners', 'properties', 'deals', 'agencies', 'profiles', 'proposals',
  'contracts', 'sequence_templates', 'document_categories', 'checklist_templates',
  'user_agencies', 'meta_leads', 'meta_oauth_connections'
];

export const AdminAuditLogs: React.FC<AdminAuditLogsProps> = ({ userProfile }) => {
  const { toast } = useModal();
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [profiles, setProfiles] = useState<UserProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedLogId, setExpandedLogId] = useState<string | null>(null);

  // Filtres
  const [filters, setFilters] = useState({
    tableName: '',
    actionType: '' as AuditActionType | '',
    userId: '',
    recordId: '',
    startDate: '',
    endDate: '',
    search: ''
  });

  // Pagination
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [limit] = useState(50);

  useEffect(() => {
    loadProfiles();
  }, []);

  useEffect(() => {
    loadLogs();
    loadCount();
  }, [filters, page]);

  const loadProfiles = async () => {
    const profilesList = await storage.getAllProfiles();
    setProfiles(profilesList);
  };

  const loadLogs = async () => {
    setLoading(true);
    try {
      const logsData = await storage.getAuditLogs({
        tableName: filters.tableName || undefined,
        actionType: filters.actionType || undefined,
        userId: filters.userId || undefined,
        recordId: filters.recordId || undefined,
        startDate: filters.startDate || undefined,
        endDate: filters.endDate || undefined,
        limit,
        offset: (page - 1) * limit
      });
      setLogs(logsData);
    } catch (error: any) {
      toast.error('Erreur lors du chargement des logs: ' + (error.message || 'Erreur inconnue'));
    } finally {
      setLoading(false);
    }
  };

  const loadCount = async () => {
    try {
      const count = await storage.getAuditLogsCount({
        tableName: filters.tableName || undefined,
        actionType: filters.actionType || undefined,
        userId: filters.userId || undefined,
        recordId: filters.recordId || undefined,
        startDate: filters.startDate || undefined,
        endDate: filters.endDate || undefined
      });
      setTotalCount(count);
    } catch (error: any) {
      console.error('Error loading count:', error);
    }
  };

  const getUserName = (userId?: string | null): string => {
    if (!userId) return 'Système';
    const profile = profiles.find(p => p.id === userId);
    if (!profile) return 'Utilisateur inconnu';
    return profile.firstName && profile.lastName
      ? `${profile.firstName} ${profile.lastName}`
      : profile.email || 'Utilisateur inconnu';
  };

  // Fonction pour formater JSON avec highlight des différences
  const formatJSONWithDiff = (oldData: any, newData: any, key: string = ''): JSX.Element[] => {
    const elements: JSX.Element[] = [];
    
    if (!oldData && !newData) {
      return [<span key="empty" className="text-slate-400 italic">Aucune donnée</span>];
    }

    // Si INSERT: tout est nouveau (vert)
    if (!oldData && newData) {
      return [
        <pre key="new" className="text-xs whitespace-pre-wrap break-all">
          {JSON.stringify(newData, null, 2).split('\n').map((line, i) => (
            <div key={i} className="bg-green-50 text-green-800 px-2 py-0.5 rounded">{line}</div>
          ))}
        </pre>
      ];
    }

    // Si DELETE: tout est supprimé (rouge)
    if (oldData && !newData) {
      return [
        <pre key="deleted" className="text-xs whitespace-pre-wrap break-all">
          {JSON.stringify(oldData, null, 2).split('\n').map((line, i) => (
            <div key={i} className="bg-red-50 text-red-800 px-2 py-0.5 rounded line-through">{line}</div>
          ))}
        </pre>
      ];
    }

    // Si UPDATE: comparer les objets
    if (oldData && newData && typeof oldData === 'object' && typeof newData === 'object') {
      const allKeys = new Set([...Object.keys(oldData || {}), ...Object.keys(newData || {})]);
      
      allKeys.forEach((k) => {
        const oldVal = oldData[k];
        const newVal = newData[k];
        
        if (oldVal === undefined && newVal !== undefined) {
          // Nouveau champ (vert)
          elements.push(
            <div key={k} className="mb-1">
              <span className="text-xs font-semibold text-green-700">+ {k}:</span>
              <span className="ml-2 text-xs bg-green-50 text-green-800 px-2 py-0.5 rounded">
                {typeof newVal === 'object' ? JSON.stringify(newVal) : String(newVal)}
              </span>
            </div>
          );
        } else if (oldVal !== undefined && newVal === undefined) {
          // Champ supprimé (rouge)
          elements.push(
            <div key={k} className="mb-1">
              <span className="text-xs font-semibold text-red-700">- {k}:</span>
              <span className="ml-2 text-xs bg-red-50 text-red-800 px-2 py-0.5 rounded line-through">
                {typeof oldVal === 'object' ? JSON.stringify(oldVal) : String(oldVal)}
              </span>
            </div>
          );
        } else if (JSON.stringify(oldVal) !== JSON.stringify(newVal)) {
          // Champ modifié (rouge pour ancien, vert pour nouveau)
          elements.push(
            <div key={k} className="mb-2">
              <span className="text-xs font-semibold text-slate-700">{k}:</span>
              <div className="ml-4 mt-1">
                <div className="mb-1">
                  <span className="text-xs text-red-600">Ancien:</span>
                  <span className="ml-2 text-xs bg-red-50 text-red-800 px-2 py-0.5 rounded line-through">
                    {typeof oldVal === 'object' ? JSON.stringify(oldVal, null, 2) : String(oldVal)}
                  </span>
                </div>
                <div>
                  <span className="text-xs text-green-600">Nouveau:</span>
                  <span className="ml-2 text-xs bg-green-50 text-green-800 px-2 py-0.5 rounded">
                    {typeof newVal === 'object' ? JSON.stringify(newVal, null, 2) : String(newVal)}
                  </span>
                </div>
              </div>
            </div>
          );
        } else {
          // Champ inchangé (gris)
          elements.push(
            <div key={k} className="mb-1">
              <span className="text-xs text-slate-500">{k}:</span>
              <span className="ml-2 text-xs text-slate-600">
                {typeof oldVal === 'object' ? JSON.stringify(oldVal) : String(oldVal)}
              </span>
            </div>
          );
        }
      });
      
      return elements.length > 0 ? elements : [
        <span key="no-changes" className="text-slate-400 italic">Aucun changement</span>
      ];
    }

    // Fallback: afficher les deux objets côte à côte
    return [
      <pre key="fallback" className="text-xs whitespace-pre-wrap break-all">
        {JSON.stringify({ old: oldData, new: newData }, null, 2)}
      </pre>
    ];
  };

  const handleFilterChange = (key: string, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(1); // Reset to first page when filters change
  };

  const handleExportCSV = () => {
    // Export all logs matching current filters (without pagination limit)
    storage.getAuditLogs({
      tableName: filters.tableName || undefined,
      actionType: filters.actionType || undefined,
      userId: filters.userId || undefined,
      recordId: filters.recordId || undefined,
      startDate: filters.startDate || undefined,
      endDate: filters.endDate || undefined,
      limit: 10000 // Export limit
    }).then(logsData => {
      const headers = ['Date', 'Utilisateur', 'Action', 'Table', 'ID Enregistrement', 'Anciennes Données', 'Nouvelles Données'];
      const rows = logsData.map(log => [
        log.createdAt ? format(new Date(log.createdAt), 'yyyy-MM-dd HH:mm:ss', { locale: fr }) : '',
        getUserName(log.userId),
        ACTION_TYPE_COLORS[log.actionType]?.label || log.actionType,
        log.tableName,
        log.recordId || '',
        log.oldData ? JSON.stringify(log.oldData, null, 2) : '',
        log.newData ? JSON.stringify(log.newData, null, 2) : ''
      ]);

      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `audit_logs_${format(new Date(), 'yyyy-MM-dd')}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success('Export CSV généré avec succès');
    }).catch(error => {
      toast.error('Erreur lors de l\'export: ' + (error.message || 'Erreur inconnue'));
    });
  };

  // Filtrer les logs selon la recherche textuelle
  const filteredLogs = logs.filter(log => {
    if (!filters.search) return true;
    const query = filters.search.toLowerCase();
    return (
      getUserName(log.userId).toLowerCase().includes(query) ||
      log.tableName.toLowerCase().includes(query) ||
      log.recordId?.toLowerCase().includes(query) ||
      ACTION_TYPE_COLORS[log.actionType]?.label.toLowerCase().includes(query)
    );
  });

  const totalPages = Math.ceil(totalCount / limit);

  return (
    <div className="p-8">
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center">
            <FileText className="w-8 h-8 text-slate-900 mr-3" />
            <h1 className="text-2xl font-bold text-slate-900">Logs d'Audit</h1>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleExportCSV}
              className="px-4 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-800 flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              Exporter CSV
            </button>
            <button
              onClick={() => { loadLogs(); loadCount(); }}
              className="px-4 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-800 flex items-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Actualiser
            </button>
          </div>
        </div>
        <p className="text-slate-500">Consultez toutes les opérations effectuées sur la base de données</p>
      </div>

      {/* Stats rapides */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-4">
          <div className="text-sm text-slate-500 mb-1">Total Logs</div>
          <div className="text-2xl font-bold text-slate-900">{totalCount.toLocaleString()}</div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-4">
          <div className="text-sm text-slate-500 mb-1">Sur cette page</div>
          <div className="text-2xl font-bold text-slate-900">{filteredLogs.length}</div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-4">
          <div className="text-sm text-slate-500 mb-1">Page</div>
          <div className="text-2xl font-bold text-slate-900">{page} / {totalPages || 1}</div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-4">
          <div className="text-sm text-slate-500 mb-1">Utilisateurs actifs</div>
          <div className="text-2xl font-bold text-slate-900">
            {new Set(logs.map(l => l.userId).filter(Boolean)).size}
          </div>
        </div>
      </div>

      {/* Filtres */}
      <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-3.5 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder="Rechercher..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
            />
          </div>
          <select
            value={filters.tableName}
            onChange={(e) => handleFilterChange('tableName', e.target.value)}
            className="px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
          >
            <option value="">Toutes les tables</option>
            {TABLE_NAMES.map(table => (
              <option key={table} value={table}>{table}</option>
            ))}
          </select>
          <select
            value={filters.actionType}
            onChange={(e) => handleFilterChange('actionType', e.target.value)}
            className="px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
          >
            <option value="">Toutes les actions</option>
            {Object.entries(ACTION_TYPE_COLORS).map(([action, config]) => (
              <option key={action} value={action}>{config.label}</option>
            ))}
          </select>
          <select
            value={filters.userId}
            onChange={(e) => handleFilterChange('userId', e.target.value)}
            className="px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
          >
            <option value="">Tous les utilisateurs</option>
            {profiles.map(profile => (
              <option key={profile.id} value={profile.id}>
                {profile.firstName && profile.lastName
                  ? `${profile.firstName} ${profile.lastName}`
                  : profile.email}
              </option>
            ))}
          </select>
          <input
            type="text"
            placeholder="ID Enregistrement"
            value={filters.recordId}
            onChange={(e) => handleFilterChange('recordId', e.target.value)}
            className="px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
          />
          <input
            type="date"
            placeholder="Date début"
            value={filters.startDate}
            onChange={(e) => handleFilterChange('startDate', e.target.value)}
            className="px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
          />
          <input
            type="date"
            placeholder="Date fin"
            value={filters.endDate}
            onChange={(e) => handleFilterChange('endDate', e.target.value)}
            className="px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
          />
        </div>
      </div>

      {/* Liste des logs */}
      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
          <p className="text-slate-500 mt-4">Chargement...</p>
        </div>
      ) : filteredLogs.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-12 text-center">
          <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500 font-medium">Aucun log trouvé</p>
          <p className="text-sm text-slate-400 mt-1">Aucun log ne correspond aux critères sélectionnés</p>
        </div>
      ) : (
        <>
          <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="p-4 text-left text-xs font-semibold text-slate-500 uppercase">Date</th>
                  <th className="p-4 text-left text-xs font-semibold text-slate-500 uppercase">Utilisateur</th>
                  <th className="p-4 text-left text-xs font-semibold text-slate-500 uppercase">Action</th>
                  <th className="p-4 text-left text-xs font-semibold text-slate-500 uppercase">Table</th>
                  <th className="p-4 text-left text-xs font-semibold text-slate-500 uppercase">ID Enregistrement</th>
                  <th className="p-4 text-left text-xs font-semibold text-slate-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredLogs.map(log => {
                  const actionConfig = ACTION_TYPE_COLORS[log.actionType] || { bg: 'bg-slate-100', text: 'text-slate-800', label: log.actionType };
                  const isExpanded = expandedLogId === log.id;
                  
                  return (
                    <React.Fragment key={log.id}>
                      <tr className="hover:bg-slate-50">
                        <td className="p-4">
                          <div className="text-sm text-slate-900">
                            {log.createdAt ? format(new Date(log.createdAt), 'dd MMM yyyy', { locale: fr }) : '—'}
                          </div>
                          <div className="text-xs text-slate-400">
                            {log.createdAt ? format(new Date(log.createdAt), 'HH:mm:ss', { locale: fr }) : '—'}
                          </div>
                        </td>
                        <td className="p-4">
                          <div className="flex items-center gap-2">
                            <User className="w-4 h-4 text-slate-400" />
                            <span className="text-sm text-slate-900">{getUserName(log.userId)}</span>
                          </div>
                        </td>
                        <td className="p-4">
                          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${actionConfig.bg} ${actionConfig.text}`}>
                            {actionConfig.label}
                          </span>
                        </td>
                        <td className="p-4">
                          <div className="flex items-center gap-2">
                            <Database className="w-4 h-4 text-slate-400" />
                            <span className="text-sm text-slate-900 font-mono">{log.tableName}</span>
                          </div>
                        </td>
                        <td className="p-4">
                          <span className="text-sm text-slate-600 font-mono">{log.recordId || '—'}</span>
                        </td>
                        <td className="p-4">
                          <button
                            onClick={() => setExpandedLogId(isExpanded ? null : log.id)}
                            className="text-amber-600 hover:text-amber-700 font-medium text-sm flex items-center gap-1"
                          >
                            {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                            {isExpanded ? 'Masquer' : 'Voir détails'}
                          </button>
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr>
                          <td colSpan={6} className="p-4 bg-slate-50">
                            {log.actionType === 'UPDATE' && log.oldData && log.newData ? (
                              // Vue diff pour UPDATE
                              <div>
                                <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                                  <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                                  Comparaison des modifications
                                </h4>
                                <div className="bg-white p-4 rounded border border-slate-200 text-xs overflow-auto max-h-96 space-y-1">
                                  {formatJSONWithDiff(log.oldData, log.newData)}
                                </div>
                                <div className="mt-3 flex gap-4 text-xs">
                                  <div className="flex items-center gap-2">
                                    <div className="w-4 h-4 bg-red-50 border border-red-200 rounded"></div>
                                    <span className="text-red-700">Ancien / Supprimé</span>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <div className="w-4 h-4 bg-green-50 border border-green-200 rounded"></div>
                                    <span className="text-green-700">Nouveau / Ajouté</span>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <div className="w-4 h-4 bg-slate-100 border border-slate-200 rounded"></div>
                                    <span className="text-slate-600">Inchangé</span>
                                  </div>
                                </div>
                              </div>
                            ) : log.actionType === 'INSERT' && log.newData ? (
                              // Vue INSERT: tout est nouveau (vert)
                              <div>
                                <h4 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                                  <span className="w-2 h-2 rounded-full bg-green-500"></span>
                                  Nouvelles Données
                                </h4>
                                <div className="bg-white p-4 rounded border border-slate-200 text-xs overflow-auto max-h-96">
                                  <pre className="whitespace-pre-wrap break-all">
                                    {JSON.stringify(log.newData, null, 2).split('\n').map((line, i) => (
                                      <div key={i} className="bg-green-50 text-green-800 px-2 py-0.5 rounded mb-0.5">
                                        {line}
                                      </div>
                                    ))}
                                  </pre>
                                </div>
                              </div>
                            ) : log.actionType === 'DELETE' && log.oldData ? (
                              // Vue DELETE: tout est supprimé (rouge)
                              <div>
                                <h4 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                                  <span className="w-2 h-2 rounded-full bg-red-500"></span>
                                  Données Supprimées
                                </h4>
                                <div className="bg-white p-4 rounded border border-slate-200 text-xs overflow-auto max-h-96">
                                  <pre className="whitespace-pre-wrap break-all">
                                    {JSON.stringify(log.oldData, null, 2).split('\n').map((line, i) => (
                                      <div key={i} className="bg-red-50 text-red-800 px-2 py-0.5 rounded mb-0.5 line-through">
                                        {line}
                                      </div>
                                    ))}
                                  </pre>
                                </div>
                              </div>
                            ) : (
                              // Vue par défaut: côte à côte
                              <div className="grid grid-cols-2 gap-6">
                                {log.oldData && (
                                  <div>
                                    <h4 className="font-semibold text-slate-900 mb-2">Anciennes Données</h4>
                                    <pre className="bg-white p-3 rounded border border-slate-200 text-xs overflow-auto max-h-64">
                                      {JSON.stringify(log.oldData, null, 2)}
                                    </pre>
                                  </div>
                                )}
                                {log.newData && (
                                  <div>
                                    <h4 className="font-semibold text-slate-900 mb-2">Nouvelles Données</h4>
                                    <pre className="bg-white p-3 rounded border border-slate-200 text-xs overflow-auto max-h-64">
                                      {JSON.stringify(log.newData, null, 2)}
                                    </pre>
                                  </div>
                                )}
                                {!log.oldData && !log.newData && (
                                  <div className="col-span-2 text-center text-slate-400 py-4">
                                    Aucune donnée disponible
                                  </div>
                                )}
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-6 flex items-center justify-between">
              <div className="text-sm text-slate-600">
                Page {page} sur {totalPages} ({totalCount} résultats au total)
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page === 1}
                  className="px-4 py-2 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Précédent
                </button>
                <button
                  onClick={() => setPage(Math.min(totalPages, page + 1))}
                  disabled={page === totalPages}
                  className="px-4 py-2 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Suivant
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};
